import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../../lib/i18n';
import { queryKeys } from '../../../lib/query-client';
import { fetchConnectors } from '../../../lib/connectors';
import { connectorDisplayName, connectorForSlug } from '../../../lib/skill-connector';
import { connectorSlugName } from '../../../lib/routine-groups';
import { type WebRoutine } from '../../../lib/routines';
import { ConnectorLogo } from '../connectors/ConnectorLogo';
import { Card, Typography } from '../ui';
import styles from './routines.module.css';

// One card per app a kit came from, holding that app's routines as rows — the Routines screen's grouping.
//
// The top level is the connector because that is the fact a reader can act on: it is the app the routines
// read, and the reason they exist at all. The SUB-groups a
// routine carries (`groupKey`) only order the rows here; their headings render on the connector's card in
// the conversation, where there is one connector and a heading cannot repeat.
//
// The last card has no connector: everything the user and the agent made themselves. It is deliberately
// last rather than first — a connector's card explains itself by its logo, and this one is the remainder.
export function RoutineConnectorCard({
  slug,
  children,
  count,
}: {
  slug?: string;
  count: number;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors });
  const connector = slug ? connectorForSlug(slug, data?.connectors) : null;

  // The connectors payload names an app in the vendor's own spelling, so it wins whenever it is there.
  // It is absent for two different reasons and the fallback covers both: the read has not landed yet,
  // and a kit whose app the connectors screen never lists at all (a Wix vertical is installed on the
  // site, not connected). Either way the slug titles the card, rather than the routines merging into
  // the unattributed one.
  const name = connector ? connectorDisplayName(connector) : connectorSlugName(slug);

  // The group is a TITLE CARD with its routines beneath it, not one card wrapping both: the title card
  // runs the full width of the list and the rows are inset from it, so the indent is what says "these
  // belong under that". The rows keep their own card surface on the page's grey — the same reading they
  // had before routines were grouped at all.
  return (
    <section className={styles.group} aria-label={name || t('routines.group.general')}>
      <Card size="sm" className={styles.groupHeadCard}>
        <div className={styles.groupHead}>
          {connector && <ConnectorLogo connector={connector} name={name} size={24} radius={6} glyphSize={14} />}
          <Typography variant="h4" className={styles.groupTitle}>
            {name || t('routines.group.general')}
          </Typography>
          <Typography variant="p3" color="secondary" className={styles.groupCount}>
            {String(count)}
          </Typography>
        </div>
      </Card>
      <div className={styles.groupRows}>{children}</div>
    </section>
  );
}
